import type { ReactNode } from 'react';
import { useEventIdentity } from '@/shared/config/useEventIdentity';
import { BrandMark } from '@/shared/ui/BrandMark';
import { Spinner } from '@/shared/ui/Spinner';

interface AuthLayoutProps {
  children: ReactNode;
}

/**
 * Layout da autenticação: um cartão só, centrado.
 *
 * Era um split de duas colunas com um painel institucional à esquerda (título, texto
 * de apresentação e lista de atributos). Saiu: é uma tela interna, aberta por quem já
 * sabe o que veio fazer, e o texto de vitrine só afastava o formulário do centro da
 * atenção. Sobrou o nome do evento e o formulário.
 *
 * O que faz a tela parecer acabada, e não vazia: hierarquia de um só título (a marca
 * em corpo grande, sem subtítulo), respiro generoso dentro do cartão, sombra em duas
 * camadas em vez de contorno forte, e dois brilhos da marca desfocados no fundo para
 * o branco do cartão ter de onde se destacar. Nada de imagem — nada a carregar.
 *
 * O nome vem do banco, via `useEventIdentity`: esta tela está fora do portão de
 * autenticação, mas o `/approval/config` é público e devolve o título.
 *
 * Enquanto esse nome não chega, o lugar do cartão fica com um spinner: o nome é a
 * única coisa que identifica de qual evento é o painel, e mostrar o formulário com a
 * reserva do build para trocá-la um instante depois fazia o cabeçalho piscar. A espera
 * termina também quando a consulta falha — aí o formulário aparece com a reserva.
 *
 * Sem `<h1>` aqui: o cabeçalho de cada etapa é dela (a de código tem o próprio), e a
 * marca acima do cartão já identifica a tela.
 */
export function AuthLayout({ children }: AuthLayoutProps) {
  const { title, loading } = useEventIdentity();

  return (
    <main className="relative grid min-h-dvh place-items-center overflow-hidden bg-surface-subtle px-5 py-12">
      {/* `fixed` para o desfoque não criar área rolável em tela curta. Opacidade alta
          o suficiente para o fundo deixar de ser um cinza plano: é contra ela que o
          branco do cartão se destaca. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed -top-48 left-1/2 size-[42rem] -translate-x-1/2 rounded-full bg-brand-500/25 blur-[150px]"
      />
      <div aria-hidden="true" className="pointer-events-none fixed -right-40 -bottom-56 size-[34rem] rounded-full bg-brand-700/20 blur-[140px]" />

      {loading ? (
        // Só o spinner, no lugar onde o cartão vai aparecer: o fundo já está
        // desenhado, então a troca não mexe no resto da tela.
        <Spinner className="size-8 text-brand-600" label="Carregando o painel" />
      ) : (
        /* Cartão e assinatura no mesmo bloco: é ele que o grid centraliza, então a
          assinatura acompanha o cartão em vez de ficar presa no pé da janela — em
          tela curta, um rodapé fixo colaria no formulário. */
        <div className="relative flex w-full max-w-[22.5rem] flex-col items-center">
          {/* Cartão de vidro na cor da marca: gradiente claro na diagonal (200 → 500),
          translúcido sobre `backdrop-blur`. O que passa por trás são os dois brilhos
          desfocados do fundo — é o que dá a impressão de espessura, sem imagem nem
          textura, e o brilho no topo (`inset-shadow`) faz a aresta de cima parecer captar
          luz.

          A cor do texto não é fixa: `--color-on-brand` é escolhido a partir da cor do
          evento — escuro sobre um cartão claro, claro sobre um cartão escuro. Fixá-lo
          como escuro deixava a tela ilegível assim que o evento escolhia uma cor
          escura, e como branco fazia o mesmo com as claras (~2,4:1 no tom padrão).

          Sem borda: quem recorta o cartão contra a página é a sombra, e um contorno
          somado ao brilho de topo dava dois traços na mesma aresta. */}
          <div className="w-full rounded-3xl bg-linear-to-br from-brand-200/80 via-brand-300/85 to-brand-500/85 p-8 shadow-elevated backdrop-blur-2xl backdrop-saturate-150 inset-shadow-[0_1px_0_rgb(255_255_255/0.45)]">
            {/* Dentro do cartão, e não acima dele: solto no fundo, o nome parecia legenda
            da página; aqui ele é o cabeçalho do que se está preenchendo.
            Sem subtítulo — uma segunda linha competia com o campo abaixo. */}
            <BrandMark title={title} subtitle="" size="md" tone="onBrand" className="mb-7 text-center" />

            {children}
          </div>

          {/* Assinatura da equipe. O coração é decorativo — o leitor de tela recebe
            "com amor" no lugar do glifo, que sairia como "coração preto". Vermelho
            do tema (o único que existe na paleta), sem sentido de status aqui. */}
          <p className="mt-6 text-center text-[0.75rem] text-brand-ink/70">
            Desenvolvido com{' '}
            <span aria-hidden="true" className="text-rejected">
              ♥
            </span>
            <span className="sr-only">amor</span> pela equipe Inteegra
          </p>
        </div>
      )}
    </main>
  );
}
